/**
 * O'Copro - Invitation Store (Zustand + Firestore)
 * Gestion des invitations avec envoi d'email via Firebase Trigger Email (SMTP2GO)
 * ✅ CORRIGÉ: Email d'invitation avec logo O'Copro et instructions professionnelles
 * src/stores/invitationStore.ts
 */

import { create } from 'zustand';
import { 
  collection, 
  addDoc, 
  serverTimestamp,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  Unsubscribe,
  Timestamp
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { sendRawEmail } from '@/services/emailService';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

export type InvitationStatus = 'pending' | 'approved' | 'rejected';
export type InviteeCategory = 'tenant' | 'owner' | 'council' | 'gardien';

export interface Invitation {
  id: string;
  name: string;
  email?: string;
  apartment?: string;
  category: InviteeCategory;
  status: InvitationStatus;
  invitedBy: string;
  invitedByName: string;
  createdAt: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
}

export interface CreateInvitationData {
  name: string;
  email?: string;
  apartment?: string;
  category: InviteeCategory;
  residenceId: string;
  residenceName: string;
  residenceCode: string;
  residenceAddress?: string;
  invitedBy: string;
  invitedByName: string;
  invitedByEmail?: string;
  autoApprove?: boolean;
}

interface InvitationState {
  invitations: Invitation[];
  isLoading: boolean;
  error: string | null;
  unsubscribe: Unsubscribe | null;
  
  // Actions
  subscribeToInvitations: (residenceId: string) => void;
  unsubscribeFromInvitations: () => void;
  createInvitation: (data: CreateInvitationData) => Promise<string | null>;
  approveInvitation: (residenceId: string, invitationId: string, approvedBy: string) => Promise<boolean>;
  rejectInvitation: (residenceId: string, invitationId: string, rejectedBy: string, reason?: string) => Promise<boolean>;
  deleteInvitation: (residenceId: string, invitationId: string) => Promise<boolean>;
  generateInviteLink: (residenceCode: string) => string;
  clearInvitations: () => void;
}

// ============================================================================
// HELPERS
// ============================================================================

const CATEGORY_LABELS: Record<InviteeCategory, string> = {
  tenant: 'Locataire',
  owner: 'Copropriétaire',
  council: 'Membre du Conseil Syndical',
  gardien: 'Gardien'
};

// ============================================================================
// STORE
// ============================================================================

export const useInvitationStore = create<InvitationState>((set, get) => ({
  invitations: [],
  isLoading: false,
  error: null,
  unsubscribe: null,

  // ========================================
  // S'abonner aux invitations en temps réel
  // ========================================
  subscribeToInvitations: (residenceId: string) => {
    if (!residenceId) return;

    // Se désabonner d'abord si déjà abonné
    const { unsubscribe: currentUnsubscribe } = get();
    if (currentUnsubscribe) {
      currentUnsubscribe();
    }

    set({ isLoading: true });

    const invitationsRef = collection(db, 'residences', residenceId, 'pendingInvitations');
    const q = query(invitationsRef);

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const invitations: Invitation[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || '',
            email: data.email || undefined,
            apartment: data.apartment || undefined,
            category: data.category || 'tenant',
            status: data.status || 'pending',
            invitedBy: data.invitedBy || '',
            invitedByName: data.invitedByName || '',
            createdAt: data.createdAt instanceof Timestamp 
              ? data.createdAt.toDate().toISOString() 
              : data.createdAt || new Date().toISOString(),
            approvedAt: data.approvedAt instanceof Timestamp 
              ? data.approvedAt.toDate().toISOString() 
              : data.approvedAt,
            approvedBy: data.approvedBy,
            rejectedAt: data.rejectedAt instanceof Timestamp 
              ? data.rejectedAt.toDate().toISOString() 
              : data.rejectedAt,
            rejectedBy: data.rejectedBy,
            rejectionReason: data.rejectionReason
          };
        });

        set({ invitations, isLoading: false, error: null });
      },
      (error) => {
        console.error('[invitationStore] Error subscribing:', error);
        set({ error: 'Erreur de connexion aux invitations', isLoading: false });
      }
    );

    set({ unsubscribe });
  },

  // ========================================
  // Se désabonner
  // ========================================
  unsubscribeFromInvitations: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
      set({ unsubscribe: null });
    }
  },

  // ========================================
  // Créer une invitation + envoyer email
  // ========================================
  createInvitation: async (data: CreateInvitationData) => {
    if (!data.residenceId) {
      console.error('[invitationStore] residenceId is required');
      return null;
    }

    try {
      // 1. Créer l'invitation dans Firestore
      const invitationsRef = collection(db, 'residences', data.residenceId, 'pendingInvitations');
      
      const invitationData: Record<string, any> = {
        name: data.name.trim(),
        email: data.email?.trim() || null,
        apartment: data.apartment?.trim() || null,
        category: data.category,
        status: data.autoApprove ? 'approved' : 'pending',
        invitedBy: data.invitedBy,
        invitedByName: data.invitedByName,
        createdAt: serverTimestamp()
      };

      if (data.autoApprove) {
        invitationData.approvedAt = serverTimestamp();
        invitationData.approvedBy = data.invitedBy;
      }

      const docRef = await addDoc(invitationsRef, invitationData);
      logger.db(' Invitation created:', docRef.id);

      // 2. Envoyer l'email si une adresse est fournie
      if (data.email?.trim()) {
        await sendInvitationEmail(data);
        logger.db(' Invitation email sent to:', data.email);
      }

      return docRef.id;
    } catch (error) {
      console.error('[invitationStore] Error creating invitation:', error);
      return null;
    }
  },

  // ========================================
  // Approuver une invitation
  // ========================================
  approveInvitation: async (residenceId: string, invitationId: string, approvedBy: string) => {
    if (!residenceId || !invitationId) return false;

    try {
      const invitationRef = doc(db, 'residences', residenceId, 'pendingInvitations', invitationId);
      
      await updateDoc(invitationRef, {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy
      });

      logger.db(' Invitation approved:', invitationId);
      return true;
    } catch (error) {
      console.error('[invitationStore] Error approving invitation:', error);
      return false;
    }
  },

  // ========================================
  // Rejeter une invitation
  // ========================================
  rejectInvitation: async (residenceId: string, invitationId: string, rejectedBy: string, reason?: string) => {
    if (!residenceId || !invitationId) return false;

    try {
      const invitationRef = doc(db, 'residences', residenceId, 'pendingInvitations', invitationId);
      
      await updateDoc(invitationRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy,
        rejectionReason: reason || null
      });

      logger.db(' Invitation rejected:', invitationId);
      return true;
    } catch (error) {
      console.error('[invitationStore] Error rejecting invitation:', error);
      return false;
    }
  },

  // ========================================
  // Supprimer une invitation
  // ========================================
  deleteInvitation: async (residenceId: string, invitationId: string) => {
    if (!residenceId || !invitationId) return false;

    try {
      const invitationRef = doc(db, 'residences', residenceId, 'pendingInvitations', invitationId);
      await deleteDoc(invitationRef);

      logger.db(' Invitation deleted:', invitationId);
      return true;
    } catch (error) {
      console.error('[invitationStore] Error deleting invitation:', error);
      return false;
    }
  },

  // ========================================
  // Générer un lien d'invitation
  // ========================================
  generateInviteLink: (residenceCode: string) => {
    const baseUrl = 'https://app.ocopro.fr';
    return `${baseUrl}/join/${residenceCode}`;
  },

  // ========================================
  // Nettoyer le store
  // ========================================
  clearInvitations: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
    }
    set({ invitations: [], unsubscribe: null });
  }
}));

// ============================================================================
// FONCTION D'ENVOI D'EMAIL (interne)
// ============================================================================

async function sendInvitationEmail(data: CreateInvitationData): Promise<void> {
  const categoryLabel = CATEGORY_LABELS[data.category] || 'Résident';

  // ✅ URL du logo O'Copro hébergé sur Firebase Storage (même que emailService.ts)
  const LOGO_URL = 'https://firebasestorage.googleapis.com/v0/b/mycopro-fb5fd.firebasestorage.app/o/assets%2Femail%2Focopro_logo.jpg?alt=media';

  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invitation O'Copro - ${data.residenceName}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f3f4f6; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f3f4f6;">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width: 600px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          
          <!-- Header avec logo O'Copro -->
          <tr>
            <td style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 28px 24px; text-align: center;">
              <img src="${LOGO_URL}" alt="O'Copro" style="height: 44px; max-width: 200px; margin-bottom: 8px; border-radius: 8px;" />
              <p style="margin: 0; color: rgba(255,255,255,0.9); font-size: 13px;">Gestion de copropriété</p>
            </td>
          </tr>
          
          <!-- Titre -->
          <tr>
            <td style="padding: 32px 24px 16px;">
              <h2 style="margin: 0 0 8px; color: #111827; font-size: 20px; font-weight: 700;">
                Vous êtes invité(e) à rejoindre une résidence
              </h2>
              <p style="margin: 0; color: #6b7280; font-size: 14px;">
                ${data.invitedByName} vous invite sur O'Copro
              </p>
            </td>
          </tr>

          <!-- Message de bienvenue -->
          <tr>
            <td style="padding: 0 24px 16px;">
              <p style="margin: 0 0 12px; color: #374151; font-size: 14px; line-height: 1.6;">
                Bonjour <strong>${data.name}</strong>,
              </p>
              <p style="margin: 0; color: #374151; font-size: 14px; line-height: 1.6;">
                <strong>${data.invitedByName}</strong> vous invite à rejoindre la résidence 
                <strong>${data.residenceName}</strong> sur l'application O'Copro en tant que <strong>${categoryLabel}</strong>.
              </p>
              ${data.residenceAddress ? `
                <p style="margin: 8px 0 0; color: #6b7280; font-size: 13px;">
                  📍 ${data.residenceAddress}
                </p>
              ` : ''}
            </td>
          </tr>

          ${data.residenceCode ? `
          <!-- Code de la résidence -->
          <tr>
            <td style="padding: 0 24px 16px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #ecfdf5; border: 2px dashed #10b981; border-radius: 12px;">
                <tr>
                  <td style="padding: 20px; text-align: center;">
                    <p style="margin: 0 0 8px; color: #059669; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Code de la résidence</p>
                    <p style="margin: 0 0 8px; color: #059669; font-size: 28px; font-weight: 700; font-family: monospace; letter-spacing: 2px;">${data.residenceCode}</p>
                    <p style="margin: 0; font-size: 12px; color: #6b7280;">Conservez ce code, il vous sera demandé lors de l'inscription</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          ` : ''}

          <!-- Instructions pour rejoindre -->
          <tr>
            <td style="padding: 0 24px 16px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f9fafb; border-radius: 12px; border: 1px solid #e5e7eb;">
                <tr>
                  <td style="padding: 20px;">
                    <p style="margin: 0 0 16px; color: #111827; font-size: 15px; font-weight: 600;">Comment rejoindre votre résidence ?</p>
                    
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="padding: 8px 0; border-bottom: 1px solid #e5e7eb;">
                          <p style="margin: 0 0 2px; color: #059669; font-size: 12px; font-weight: 600; text-transform: uppercase;">Accéder à l'application</p>
                          <p style="margin: 0; color: #374151; font-size: 14px;">Rendez-vous sur <strong>app.ocopro.fr</strong> ou téléchargez l'application O'Copro sur votre smartphone.</p>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 8px 0; border-bottom: 1px solid #e5e7eb;">
                          <p style="margin: 0 0 2px; color: #059669; font-size: 12px; font-weight: 600; text-transform: uppercase;">Créer votre compte</p>
                          <p style="margin: 0; color: #374151; font-size: 14px;">Inscrivez-vous avec l'adresse email <strong>${data.email}</strong> pour que votre invitation soit automatiquement reconnue.</p>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 8px 0; border-bottom: 1px solid #e5e7eb;">
                          <p style="margin: 0 0 2px; color: #059669; font-size: 12px; font-weight: 600; text-transform: uppercase;">Rejoindre la résidence</p>
                          <p style="margin: 0; color: #374151; font-size: 14px;">Entrez le code de la résidence${data.residenceCode ? ` <strong>${data.residenceCode}</strong>` : ''} lorsqu'il vous est demandé.</p>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 8px 0;">
                          <p style="margin: 0 0 2px; color: #059669; font-size: 12px; font-weight: 600; text-transform: uppercase;">Compléter votre profil</p>
                          <p style="margin: 0; color: #374151; font-size: 14px;">Renseignez vos informations et commencez à utiliser O'Copro.</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Bouton CTA -->
          <tr>
            <td style="padding: 0 24px 8px; text-align: center;">
              <a href="https://app.ocopro.fr/join/${data.residenceCode}?r=${data.residenceCode}&c=${encodeURIComponent(data.category)}&n=${encodeURIComponent(data.name)}&a=${encodeURIComponent(data.apartment || '')}" style="display: inline-block; background: linear-gradient(135deg, #10b981, #059669); color: #ffffff; font-size: 15px; font-weight: 600; padding: 14px 32px; border-radius: 10px; text-decoration: none;">
                Rejoindre ma résidence
              </a>
            </td>
          </tr>
          <tr>
            <td style="padding: 0 24px 24px; text-align: center;">
              <p style="margin: 0; color: #6b7280; font-size: 12px;">Ou copiez ce lien dans votre navigateur :<br>
                <a href="https://app.ocopro.fr/join/${data.residenceCode}?r=${data.residenceCode}&c=${encodeURIComponent(data.category)}&n=${encodeURIComponent(data.name)}&a=${encodeURIComponent(data.apartment || '')}" style="color: #059669; word-break: break-all;">
                  https://app.ocopro.fr/join/${data.residenceCode}
                </a>
              </p>
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="padding: 24px; background-color: #f9fafb; border-top: 1px solid #e5e7eb; text-align: center;">
              <p style="margin: 0 0 8px; color: #6b7280; font-size: 12px;">
                Cet email a été envoyé par ${data.invitedByName} via O'Copro.
              </p>
              <p style="margin: 0; color: #9ca3af; font-size: 11px;">
                © ${new Date().getFullYear()} O'Copro - Gestion de copropriété simplifiée
              </p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`.trim();

  const emailData: Record<string, any> = {
    to: data.email,
    message: {
      subject: `🏠 ${data.invitedByName} vous invite à rejoindre ${data.residenceName} sur O'Copro`,
      html: htmlContent
    },
    createdAt: serverTimestamp()
  };

  // Ajouter l'inviteur en copie (CC) s'il a une adresse email
  if (data.invitedByEmail) {
    emailData.cc = data.invitedByEmail;
  }

  await sendRawEmail(emailData);
}

export default useInvitationStore;
